import { uiText } from "@/lib/i18n/uiText";
import { useEffect, useState } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTimezone, COMMON_TIMEZONES } from "@/contexts/TimezoneContext";
import { cn } from "@/lib/utils";

interface TimezoneSelectorProps {
  variant?: "full" | "compact";
  showLabel?: boolean;
}

export function TimezoneSelector({ variant = "compact", showLabel = true }: TimezoneSelectorProps) {
  const { timezone, timezoneAbbr, setTimezone, isAutoDetect, deviceTimezone } = useTimezone();

  // The server has no device timezone, so the first client render must match the
  // server markup ("UTC") and only then swap in the visitor's real zone.
  // Rendering it straight away throws a hydration mismatch and blanks the page.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const abbr = hydrated ? timezoneAbbr : "UTC";

  const getCurrentLabel = () => {
    if (!hydrated) return "UTC";
    if (isAutoDetect) {
      return `Auto (${timezoneAbbr})`;
    }
    const found = COMMON_TIMEZONES.find(tz => tz.value === timezone);
    return found ? found.label : timezoneAbbr;
  };

  if (variant === "compact") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <Globe className="h-3.5 w-3.5" />
            <span>{abbr}</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-64 max-h-80 overflow-y-auto bg-popover border border-border shadow-lg z-50"
        >
          <DropdownMenuLabel className="text-xs text-muted-foreground">
             {uiText("Select Timezone")} </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {COMMON_TIMEZONES.map((tz) => {
            const isSelected = isAutoDetect 
              ? tz.value === "auto" 
              : tz.value === timezone;
            
            return (
              <DropdownMenuItem
                key={tz.value}
                onClick={() => setTimezone(tz.value)}
                className={cn(
                  "flex items-center justify-between cursor-pointer",
                  isSelected && "bg-primary/10"
                )}
              >
                <span className="text-sm">
                  {uiText(tz.label)}
                  {tz.value === "auto" && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({deviceTimezone})
                    </span>
                  )}
                </span>
                {isSelected && <Check className="h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Full variant for settings page
  return (
    <div className="space-y-2">
      {showLabel && (
        <label className="text-sm font-medium">{uiText("Timezone")}</label>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-between"
          >
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span>{getCurrentLabel()}</span>
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="start" 
          className="w-72 max-h-80 overflow-y-auto bg-popover border border-border shadow-lg z-50"
        >
          <DropdownMenuLabel>{uiText("Select Timezone")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {COMMON_TIMEZONES.map((tz) => {
            const isSelected = isAutoDetect 
              ? tz.value === "auto" 
              : tz.value === timezone;
            
            return (
              <DropdownMenuItem
                key={tz.value}
                onClick={() => setTimezone(tz.value)}
                className={cn(
                  "flex items-center justify-between cursor-pointer",
                  isSelected && "bg-primary/10"
                )}
              >
                <span>
                  {uiText(tz.label)}
                  {tz.value === "auto" && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({deviceTimezone})
                    </span>
                  )}
                </span>
                {isSelected && <Check className="h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      <p className="text-xs text-muted-foreground">
         {uiText("Match times will be displayed in your selected timezone")} </p>
    </div>
  );
}
